import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';
import { flatten } from 'lodash';

import { stripesConnect } from '@folio/stripes/core';

import TeamForm from '../../components/team/team-form';
import { useCallout } from '../../../hooks';

import {
  getCreateEditTeamPayload,
} from '../../../utils';
import {
  TEAM_FIELDS,
  TEAM_INITIAL_VALUES,
  LOAD_LIMIT,
  getTeamViewUrl,
  STATUS_CODE_DUPLICATE,
} from '../../../constants';
import { IfPermissionWrapper } from '../../../components/common';

const TeamEditRoute = ({
  mutator,
  history,
  location,
}) => {
  const [team, setTeam] = useState({});
  const [isTeamLoaded, setIsTeamLoaded] = useState(false);
  const [isTeamFailed, setIsTeamFailed] = useState(false);

  const formatInitialValues = (teamFieldValues) => {
    return {
      [TEAM_FIELDS.ID]: teamFieldValues[TEAM_FIELDS.ID],
      [TEAM_FIELDS.NAME]: teamFieldValues[TEAM_FIELDS.NAME],
      [TEAM_FIELDS.DESCRIPTION]: teamFieldValues[TEAM_FIELDS.DESCRIPTION] || TEAM_INITIAL_VALUES[TEAM_FIELDS.DESCRIPTION],
      [TEAM_FIELDS.MEMBERS]: teamFieldValues[TEAM_FIELDS.MEMBERS] || TEAM_INITIAL_VALUES[TEAM_FIELDS.MEMBERS],
      [TEAM_FIELDS.CHANGE_VERSION]: teamFieldValues[TEAM_FIELDS.CHANGE_VERSION],
    };
  };

  useEffect(
    () => {
      let isSubscribed = true;

      mutator.editTask.GET()
        .then(teamResponse => {
          if (isSubscribed) {
            const userIds = teamResponse.members || [];
            const usersPromises = [];

            while (userIds.length) {
              usersPromises.push(mutator.getUsers.GET({
                params: {
                  limit: LOAD_LIMIT,
                  query: userIds.splice(0, LOAD_LIMIT)
                    .map(userId => `id==${userId}`)
                    .join(' or '),
                },
              }));
            }

            return Promise.all(usersPromises)
              .then(userArrays => {
                teamResponse.members = flatten(userArrays);
                setTeam(formatInitialValues(teamResponse));
              });
          } else {
            return Promise.all([]);
          }
        })
        .catch(() => {
          if (isSubscribed) {
            setIsTeamFailed(true);
          }
        })
        .then(() => {
          if (isSubscribed) {
            setIsTeamLoaded(true);
          }
        });

      return () => {
        isSubscribed = false;
      };
    },
    [],
  );

  const showCallout = useCallout();

  const onBack = useCallback(() => {
    history.push({
      pathname: getTeamViewUrl(team[TEAM_FIELDS.ID]),
      search: location.search,
    });
  }, [history, location.search, team[TEAM_FIELDS.ID]]);

  const onSubmit = useCallback((values) => {
    const requestBody = {
      ...getCreateEditTeamPayload(values),
      [TEAM_FIELDS.ID]: team[TEAM_FIELDS.ID],
    };

    mutator.editTask.PUT({ ...requestBody })
      .then((response) => {
        showCallout({
          message: <FormattedMessage
            id="ui-task-list.callout.updated.team"
            values={{ name: response[TEAM_FIELDS.NAME] }}
          />,
        });
        history.push({
          pathname: getTeamViewUrl(response[TEAM_FIELDS.ID]),
          search: location.search,
        });
      })
      .catch((error) => {
        let calloutMessageId = 'validation.communicationProblem';

        if (error.status === STATUS_CODE_DUPLICATE) {
          calloutMessageId = 'error.duplicate.team';
        }

        showCallout({
          type: 'error',
          message: <FormattedMessage id={`ui-task-list.${calloutMessageId}`} />,
        });
      });
  }, [team[TEAM_FIELDS.ID], history, location.search, mutator.editTask, showCallout]);

  const getTitle = () => (
    <FormattedMessage
      id="ui-task-list.title.edit.team"
      values={{ name: team[TEAM_FIELDS.NAME] }}
    />
  );

  return (
    <IfPermissionWrapper perm="ui-task-list.teams.create-edit">
      <TeamForm
        formTitle={getTitle}
        initialValues={team}
        isFailed={isTeamFailed}
        isLoading={!isTeamLoaded}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    </IfPermissionWrapper>
  );
};

TeamEditRoute.manifest = Object.freeze({
  editTask: {
    type: 'okapi',
    path: 'tasks/teams/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    PUT: { path: 'tasks/teams/:{id}' },
    accumulate: true,
    fetch: false,
  },
  getUsers: {
    type: 'okapi',
    path: 'users',
    records: 'users',
    accumulate: true,
    fetch: false,
  },
});

TeamEditRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  mutator: PropTypes.shape({
    editTask: PropTypes.shape({
      GET: PropTypes.func.isRequired,
      PUT: PropTypes.func.isRequired,
    }),
    getUsers: PropTypes.shape({
      GET: PropTypes.func.isRequired,
    }),
  }),
};

export default stripesConnect(TeamEditRoute);
